/**
 * Organization Profile Service
 *
 * Handles API calls for organization profile management (manager operations).
 */

const API_URL = process.env.REACT_APP_API_URL || '';

/**
 * Get current user info from Azure SWA authentication or demo session
 */
const getCurrentUser = async () => {
  // Check for demo session first
  const demoToken = sessionStorage.getItem('demoToken');
  if (demoToken) {
    const demoSession = sessionStorage.getItem('demoSession');
    if (demoSession) {
      try {
        const session = JSON.parse(demoSession);
        return {
          id: session.user.user_id,
          email: session.user.email,
          name: session.user.name,
          provider: 'demo',
          roles: [],
          isDemoMode: true
        };
      } catch (e) {
        console.error('Error parsing demo session:', e);
      }
    }
  }

  // OAuth authentication
  try {
    const response = await fetch('/.auth/me');
    if (response.ok) {
      const authData = await response.json();
      const userInfo = authData.clientPrincipal;
      if (userInfo) {
        return {
          id: userInfo.userId,
          email: userInfo.userDetails,
          name: userInfo.userClaims?.find(claim => claim.typ === 'name')?.val || userInfo.userDetails,
          provider: userInfo.identityProvider,
          roles: userInfo.userRoles,
          isDemoMode: false
        };
      }
    }
  } catch (error) {
    console.error('Error fetching user info:', error);
  }
  return null;
};

/**
 * Get organization profile for current user's organization
 */
export const getOrganizationProfile = async () => {
  try {
    const user = await getCurrentUser();
    if (!user || !user.email) {
      throw new Error('User not authenticated');
    }

    const headers = {
      'Content-Type': 'application/json'
    };

    // Add demo token or OAuth email
    if (user.isDemoMode) {
      const demoToken = sessionStorage.getItem('demoToken');
      if (demoToken) {
        headers['Authorization'] = `Bearer ${demoToken}`;
      }
    } else {
      headers['X-User-Email'] = user.email;
    }

    const response = await fetch(`${API_URL}/api/organization/profile`, {
      method: 'GET',
      headers
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || `HTTP ${response.status}: Failed to fetch organization profile`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error fetching organization profile:', error);
    throw error;
  }
};

/**
 * Update organization profile (manager-only operation)
 */
export const updateOrganizationProfile = async (profileData) => {
  try {
    const user = await getCurrentUser();
    if (!user || !user.email) {
      throw new Error('User not authenticated');
    }

    const headers = {
      'Content-Type': 'application/json'
    };

    // Add demo token or OAuth email
    if (user.isDemoMode) {
      const demoToken = sessionStorage.getItem('demoToken');
      if (demoToken) {
        headers['Authorization'] = `Bearer ${demoToken}`;
      }
    } else {
      headers['X-User-Email'] = user.email;
    }

    const response = await fetch(`${API_URL}/api/organization/profile`, {
      method: 'PUT',
      headers,
      body: JSON.stringify(profileData)
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || `HTTP ${response.status}: Failed to update organization profile`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error updating organization profile:', error);
    throw error;
  }
};

/**
 * Get validation options for organization profile fields
 */
export const getOrganizationProfileValidationOptions = async () => {
  try {
    const response = await fetch(`${API_URL}/api/organization/profile/validation-options`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || `HTTP ${response.status}: Failed to fetch validation options`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error fetching validation options:', error);
    throw error;
  }
};
